import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import api from "../api/axios";
import { runBackgroundRequest } from "../utils/backgroundRequestQueue";
import { useEventHostAttention } from "./EventHostAttentionContext";

const STORAGE_KEY_PREFIX = "vg_required_actions_dismissed_v2";
const PREMIUM_BENEFITS_ACTION_TYPE = "premium_monthly_benefits";
const PREMIUM_BENEFITS_DELAY_MS = 10 * 60 * 1000;
const SUPPORTED_REQUIRED_ACTION_TYPES = [
  "premium_monthly_benefits",
  "host_processing_invoice_due",
  "approved_request_payment",
];
const PREMIUM_BENEFITS_DEFERRED_KEY_PREFIX = "vg_premium_benefits_deferred_v1";
const APP_ENTRY_ID_STORAGE_KEY = "vg_app_entry_id_v1";
const SESSION_ONLY_ACTION_TYPES = new Set(["join_party_session"]);
const BACKGROUND_REFRESH_DEBOUNCE_MS = 400;
const BACKGROUND_REFRESH_COOLDOWN_MS = 1500;

const normalizePathname = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";
  try {
    const base = typeof window !== "undefined" ? window.location.origin : "https://vaultgrid.local";
    const pathname = new URL(rawValue, base).pathname;
    return String(pathname || "/").replace(/\/+$/, "") || "/";
  } catch {
    return rawValue.split(/[?#]/, 1)[0].replace(/\/+$/, "") || "/";
  }
};

export const isJoinSessionActionForPath = (action, pathname) => {
  if (action?.type !== "join_party_session") return false;
  const eventId = Number(action?.event?.id);
  const fallbackPath = Number.isFinite(eventId) && eventId > 0
    ? `/events/${eventId}/party-session`
    : "";
  const sessionPath = normalizePathname(action?.payload?.session_path || fallbackPath);
  const currentPath = normalizePathname(pathname);
  return Boolean(
    sessionPath
    && currentPath
    && (currentPath === sessionPath || currentPath.startsWith(`${sessionPath}/`))
  );
};

const storageKeyForUser = (user) => {
  const userId = user?.id;
  return userId ? `${STORAGE_KEY_PREFIX}:${userId}` : null;
};

const readDismissedIds = (user) => {
  if (typeof window === "undefined") return [];
  const storageKey = storageKeyForUser(user);
  if (!storageKey) return [];

  try {
    const rawValue = window.localStorage.getItem(storageKey);
    if (!rawValue) return [];
    const parsed = JSON.parse(rawValue);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch (err) {
    console.warn("[RequiredActionsContext] failed to read dismissed ids", err);
    return [];
  }
};

const writeDismissedIds = (user, actionIds) => {
  if (typeof window === "undefined") return;
  const storageKey = storageKeyForUser(user);
  if (!storageKey) return;

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(actionIds || []));
  } catch (err) {
    console.warn("[RequiredActionsContext] failed to persist dismissed ids", err);
  }
};

const createEntryId = () => `${Date.now()}:${Math.random().toString(36).slice(2)}`;

const getAppEntryId = () => {
  if (typeof window === "undefined") return "server";
  try {
    const existing = String(
      window.sessionStorage.getItem(APP_ENTRY_ID_STORAGE_KEY) || ""
    ).trim();
    if (existing) return existing;
    const created = createEntryId();
    window.sessionStorage.setItem(APP_ENTRY_ID_STORAGE_KEY, created);
    return created;
  } catch {
    return "browser-session";
  }
};

const premiumBenefitsDeferredKey = (userId, actionId) => (
  `${PREMIUM_BENEFITS_DEFERRED_KEY_PREFIX}:${userId}:${actionId}`
);

const premiumBenefitsCanSurface = (
  userId,
  action,
  { allowCurrentEntryAfterDelay = false, nowMs = Date.now() } = {}
) => {
  if (action?.type !== PREMIUM_BENEFITS_ACTION_TYPE) return true;
  if (typeof window === "undefined" || !userId || !action?.id) return false;

  const entryId = getAppEntryId();
  const storageKey = premiumBenefitsDeferredKey(userId, action.id);
  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) || "null");
    const firstSeenAt = Number(parsed?.firstSeenAt || 0);
    const firstEntryId = String(parsed?.firstEntryId || "");
    const payloadSurfaceAfterAt = Date.parse(
      String(action?.payload?.surface_after || "")
    );
    if (!firstSeenAt || !firstEntryId) {
      const surfaceAfterAt = Number.isFinite(payloadSurfaceAfterAt)
        ? payloadSurfaceAfterAt
        : nowMs + PREMIUM_BENEFITS_DELAY_MS;
      window.localStorage.setItem(storageKey, JSON.stringify({
        firstSeenAt: nowMs,
        firstEntryId: entryId,
        surfaceAfterAt,
        allowFirstEntryAfterDelay: Boolean(allowCurrentEntryAfterDelay),
      }));
      return Boolean(allowCurrentEntryAfterDelay && nowMs >= surfaceAfterAt);
    }
    const surfaceAfterAt = Number(parsed?.surfaceAfterAt || 0)
      || (
        Number.isFinite(payloadSurfaceAfterAt)
          ? payloadSurfaceAfterAt
          : firstSeenAt + PREMIUM_BENEFITS_DELAY_MS
      );
    return (
      nowMs >= surfaceAfterAt
      && (
        firstEntryId !== entryId
        || Boolean(parsed?.allowFirstEntryAfterDelay)
      )
    );
  } catch {
    return false;
  }
};

export const RequiredActionsContext = createContext({
  actions: [],
  loading: false,
  open: false,
  hasPending: false,
  dismissForSession: () => {},
  dismissPersistently: () => {},
  focusedActionId: null,
  openPendingActions: () => false,
  openActionByNotificationId: () => Promise.resolve(false),
  setOpen: () => {},
  refresh: () => Promise.resolve([]),
  consumeAction: () => {},
});

export const RequiredActionsProvider = ({ user, currentPathname = "", children }) => {
  const userId = user?.id || null;
  const { data: hostAttentionData } = useEventHostAttention();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialFetchedUserId, setInitialFetchedUserId] = useState(null);
  const [open, setOpen] = useState(false);
  const [dismissedThisSession, setDismissedThisSession] = useState(false);
  const [dismissedPersistedIds, setDismissedPersistedIds] = useState([]);
  const [focusedActionId, setFocusedActionId] = useState(null);
  const consumedActionIdsRef = useRef([]);
  const actionsRef = useRef([]);
  const serverActionsRef = useRef([]);
  const cacheVersionRef = useRef("");
  const backgroundFetchRef = useRef(null);
  const lastFetchStartedAtRef = useRef(0);
  const activeUserIdRef = useRef(userId);
  const previousModalActionSignatureRef = useRef("");
  const modalActionsUserIdRef = useRef(null);
  const initialFetchCompletedRef = useRef(false);
  const premiumAckSyncAttemptedRef = useRef(new Set());

  activeUserIdRef.current = userId;

  useEffect(() => {
    actionsRef.current = actions;
  }, [actions]);

  const fetchActions = useCallback(
    async ({
      silent = false,
      suppressSessionPromptForEventId = null,
    } = {}) => {
      if (!userId) {
        cacheVersionRef.current = "";
        setActions([]);
        setInitialFetchedUserId(null);
        setOpen(false);
        return [];
      }

      if (!silent) setLoading(true);
      try {
        lastFetchStartedAtRef.current = Date.now();
        const knownVersion = cacheVersionRef.current;
        const { data, headers } = await runBackgroundRequest(() => (
          api.get("/events/required-actions/", {
            params: {
              supported_types: SUPPORTED_REQUIRED_ACTION_TYPES.join(","),
              ...(knownVersion ? { known_version: knownVersion } : {}),
            },
            meta: { suppressGlobalError: true },
          })
        ));
        if (activeUserIdRef.current !== userId) {
          return [];
        }
        const responseVersion = String(
          (typeof headers?.get === "function"
            ? headers.get("x-required-actions-version")
            : headers?.["x-required-actions-version"])
          || ""
        ).trim();
        if (responseVersion) {
          cacheVersionRef.current = responseVersion;
        }
        if (data?.not_modified === true) {
          const nextActions = serverActionsRef.current.filter(
            (item) => premiumBenefitsCanSurface(userId, item, {
              allowCurrentEntryAfterDelay: !initialFetchCompletedRef.current,
            })
          );
          initialFetchCompletedRef.current = true;
          setActions(nextActions);
          return nextActions;
        }
        const consumedIds = consumedActionIdsRef.current;
        const serverActions = (Array.isArray(data?.actions) ? data.actions : []).filter(
          (item) => !consumedIds.includes(String(item?.id || ""))
        );
        serverActionsRef.current = serverActions;
        const locallyDismissedIds = new Set(readDismissedIds({ id: userId }));
        serverActions.forEach((item) => {
          const actionId = String(item?.id || "");
          if (
            item?.type !== PREMIUM_BENEFITS_ACTION_TYPE
            || !locallyDismissedIds.has(actionId)
            || premiumAckSyncAttemptedRef.current.has(actionId)
          ) return;
          const monthKey = String(item?.payload?.month_key || actionId.split(":").pop() || "");
          if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(monthKey)) return;
          premiumAckSyncAttemptedRef.current.add(actionId);
          void api.post(`/events/required-actions/premium-benefits/${monthKey}/ack/`)
            .catch(() => {});
        });
        const nextActions = serverActions.filter(
          (item) => premiumBenefitsCanSurface(userId, item, {
            allowCurrentEntryAfterDelay: !initialFetchCompletedRef.current,
          })
        );
        initialFetchCompletedRef.current = true;
        const shouldSuppressSessionPrompt = nextActions.some(
          (item) =>
            item?.type === "join_party_session"
            && Number(item?.event?.id) === Number(suppressSessionPromptForEventId)
        );
        if (shouldSuppressSessionPrompt) {
          setDismissedThisSession(true);
          setOpen(false);
        }
        setActions(nextActions);
        return nextActions;
      } catch (err) {
        console.error("[RequiredActionsContext] failed to load actions", err);
        return [];
      } finally {
        if (activeUserIdRef.current === userId) {
          setInitialFetchedUserId(userId);
          if (!silent) setLoading(false);
        }
      }
    },
    [userId]
  );

  const refreshInBackground = useCallback(() => {
    if (!userId) return Promise.resolve([]);

    const pendingFetch = backgroundFetchRef.current;
    if (pendingFetch?.userId === userId) {
      return pendingFetch.promise;
    }
    if (
      Date.now() - lastFetchStartedAtRef.current
      < BACKGROUND_REFRESH_COOLDOWN_MS
    ) {
      return Promise.resolve(actionsRef.current);
    }

    const promise = fetchActions({ silent: true });
    const pendingEntry = { userId, promise };
    backgroundFetchRef.current = pendingEntry;
    void promise.finally(() => {
      if (backgroundFetchRef.current === pendingEntry) {
        backgroundFetchRef.current = null;
      }
    });
    return promise;
  }, [fetchActions, userId]);

  useEffect(() => {
    if (!userId) {
      cacheVersionRef.current = "";
      backgroundFetchRef.current = null;
      lastFetchStartedAtRef.current = 0;
      setActions([]);
      setInitialFetchedUserId(null);
      setOpen(false);
      setDismissedThisSession(false);
      setDismissedPersistedIds([]);
      setFocusedActionId(null);
      consumedActionIdsRef.current = [];
      serverActionsRef.current = [];
      initialFetchCompletedRef.current = false;
      premiumAckSyncAttemptedRef.current = new Set();
      setLoading(false);
      return;
    }

    cacheVersionRef.current = "";
    backgroundFetchRef.current = null;
    lastFetchStartedAtRef.current = 0;
    setDismissedThisSession(false);
    setInitialFetchedUserId(null);
    setFocusedActionId(null);
    consumedActionIdsRef.current = [];
    serverActionsRef.current = [];
    initialFetchCompletedRef.current = false;
    premiumAckSyncAttemptedRef.current = new Set();
    setDismissedPersistedIds(readDismissedIds({ id: userId }));
    void fetchActions();
  }, [userId, fetchActions]);

  const hostActions = useMemo(() => {
    if (!userId || initialFetchedUserId !== userId) return [];
    const requestEvents = hostAttentionData?.participant_requests?.events || [];
    const waitlistEvents = hostAttentionData?.waitlist?.events || [];
    const mapped = [];

    requestEvents.forEach((item) => {
      const eventId = Number(item?.event_id);
      const count = Number(item?.unread_count ?? item?.pending_count) || 0;
      if (!Number.isInteger(eventId) || eventId <= 0 || count <= 0) return;
      mapped.push({
        id: `host_participant_requests:${eventId}`,
        type: "host_participant_requests",
        event: {
          id: eventId,
          name: item?.event_name || "",
          status: "active",
          icon_image_url: item?.icon_image_url || null,
          badge_label: item?.badge_label || null,
        },
        payload: { count },
      });
    });

    waitlistEvents.forEach((item) => {
      const eventId = Number(item?.event_id);
      const count = Number(item?.unread_count) || 0;
      if (!Number.isInteger(eventId) || eventId <= 0 || count <= 0) return;
      mapped.push({
        id: `host_waitlist_activity:${eventId}`,
        type: "host_waitlist_activity",
        event: {
          id: eventId,
          name: item?.event_name || "",
          status: "active",
          icon_image_url: item?.icon_image_url || null,
          badge_label: item?.badge_label || null,
        },
        payload: { count },
      });
    });
    return mapped;
  }, [hostAttentionData, initialFetchedUserId, userId]);

  const modalActions = useMemo(
    () =>
      [...actions, ...hostActions].filter(
        (item) =>
          item?.type !== "vault_profile_prompt"
          && !isJoinSessionActionForPath(item, currentPathname)
          && (
            SESSION_ONLY_ACTION_TYPES.has(item?.type)
            || !dismissedPersistedIds.includes(item?.id)
          )
      ),
    [actions, currentPathname, dismissedPersistedIds, hostActions]
  );

  const modalActionSignature = useMemo(
    () =>
      modalActions
        .map((item) => String(item?.id || "").trim())
        .filter(Boolean)
        .sort()
        .join("|"),
    [modalActions]
  );

  useEffect(() => {
    const normalizedUserId = String(userId || "");

    // A user switch may briefly render the previous user's actions before the
    // reset effect is committed. Record that snapshot without opening it.
    if (modalActionsUserIdRef.current !== normalizedUserId) {
      modalActionsUserIdRef.current = normalizedUserId;
      previousModalActionSignatureRef.current = modalActionSignature;
      if (!modalActionSignature) setOpen(false);
      return;
    }

    const previousSignature = previousModalActionSignatureRef.current;
    const actionsChanged = previousSignature !== modalActionSignature;
    previousModalActionSignatureRef.current = modalActionSignature;

    if (!modalActionSignature) {
      setOpen(false);
      return;
    }

    // Silent polling returns fresh object instances every minute. Reopen only
    // when the actionable IDs actually changed; otherwise a temporarily hidden
    // modal would interrupt an in-progress participant evaluation.
    if (actionsChanged && !dismissedThisSession) {
      setOpen(true);
    }
  }, [dismissedThisSession, modalActionSignature, userId]);

  useEffect(() => {
    if (!userId) return undefined;

    let backgroundRefreshTimer = null;
    const scheduleBackgroundRefresh = () => {
      if (backgroundRefreshTimer !== null) {
        window.clearTimeout(backgroundRefreshTimer);
      }
      backgroundRefreshTimer = window.setTimeout(() => {
        backgroundRefreshTimer = null;
        void refreshInBackground();
      }, BACKGROUND_REFRESH_DEBOUNCE_MS);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") scheduleBackgroundRefresh();
    };
    const onNotification = async () => {
      const previousActionIds = new Set(
        actionsRef.current.map((item) => String(item?.id || ""))
      );
      const nextActions = await fetchActions({ silent: true });
      const hasNewUrgentAction = nextActions.some(
        (item) =>
          !previousActionIds.has(String(item?.id || ""))
          && [
            "slot_confirmation",
            "activation_date_confirmation",
            "intent_confirmation",
            "approved_request_payment",
          ].includes(item?.type)
      );
      if (hasNewUrgentAction) {
        setDismissedThisSession(false);
        setOpen(true);
      }
    };

    window.addEventListener("focus", scheduleBackgroundRefresh);
    window.addEventListener("vg:notification", onNotification);
    document.addEventListener("visibilitychange", onVisibilityChange);
    const timer = window.setInterval(() => {
      scheduleBackgroundRefresh();
    }, 60 * 1000);

    return () => {
      window.removeEventListener("focus", scheduleBackgroundRefresh);
      window.removeEventListener("vg:notification", onNotification);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.clearInterval(timer);
      if (backgroundRefreshTimer !== null) {
        window.clearTimeout(backgroundRefreshTimer);
      }
    };
  }, [userId, fetchActions, refreshInBackground]);

  const dismissForSession = useCallback(() => {
    setDismissedThisSession(true);
    setOpen(false);
  }, []);

  const dismissPersistently = useCallback(
    (actionIds) => {
      const normalizedIds = (Array.isArray(actionIds) ? actionIds : [actionIds])
        .map((item) => String(item || "").trim())
        .filter(Boolean);
      if (!normalizedIds.length) {
        setDismissedThisSession(true);
        setOpen(false);
        return;
      }

      setDismissedPersistedIds((prev) => {
        const nextIds = Array.from(new Set([...prev, ...normalizedIds]));
        writeDismissedIds({ id: userId }, nextIds);
        return nextIds;
      });
      setDismissedThisSession(true);
      setOpen(false);
    },
    [userId]
  );

  const consumeAction = useCallback((actionId) => {
    if (!actionId) return;
    const normalizedActionId = String(actionId);
    if (!consumedActionIdsRef.current.includes(normalizedActionId)) {
      consumedActionIdsRef.current = [...consumedActionIdsRef.current, normalizedActionId];
    }
    setActions((prev) => prev.filter((item) => String(item?.id || "") !== normalizedActionId));
    serverActionsRef.current = serverActionsRef.current.filter(
      (item) => String(item?.id || "") !== normalizedActionId
    );
    setDismissedPersistedIds((prev) => {
      const nextIds = prev.filter((item) => String(item) !== normalizedActionId);
      writeDismissedIds({ id: userId }, nextIds);
      return nextIds;
    });
    setFocusedActionId((prev) => (
      String(prev || "") === normalizedActionId ? null : prev
    ));
  }, [userId]);

  const openPendingActions = useCallback(() => {
    if (!modalActions.length) return false;

    setDismissedThisSession(false);
    setFocusedActionId(null);
    setOpen(true);
    return true;
  }, [modalActions]);

  const openActionByNotificationId = useCallback(async (notificationId) => {
    const normalizedNotificationId = Number(notificationId);
    if (!Number.isFinite(normalizedNotificationId) || normalizedNotificationId <= 0) {
      return false;
    }

    const nextActions = await fetchActions({ silent: true });
    const targetAction = nextActions.find(
      (item) => Number(item?.payload?.notification_id) === normalizedNotificationId
    );
    if (!targetAction?.id) return false;

    setDismissedPersistedIds((prev) => {
      const nextIds = prev.filter(
        (item) => String(item) !== String(targetAction.id)
      );
      writeDismissedIds({ id: userId }, nextIds);
      return nextIds;
    });
    setDismissedThisSession(false);
    setFocusedActionId(String(targetAction.id));
    setOpen(true);
    return true;
  }, [fetchActions, userId]);

  const contextValue = useMemo(
    () => ({
      actions: modalActions,
      loading,
      open,
      hasPending: modalActions.length > 0,
      focusedActionId,
      dismissForSession,
      dismissPersistently,
      openPendingActions,
      openActionByNotificationId,
      setOpen,
      refresh: fetchActions,
      consumeAction,
    }),
    [
      modalActions,
      loading,
      open,
      focusedActionId,
      dismissForSession,
      dismissPersistently,
      openPendingActions,
      openActionByNotificationId,
      fetchActions,
      consumeAction,
    ]
  );

  return (
    <RequiredActionsContext.Provider value={contextValue}>
      {children}
    </RequiredActionsContext.Provider>
  );
};
